import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";

export interface SearchableSelectOption {
  value: string;
  label: string;
  group?: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  className?: string;
  labelClassName?: string;
  id?: string;
  name?: string;
  required?: boolean;
  clearable?: boolean;
  disabled?: boolean;
}

export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "Select an option",
  label,
  error,
  className,
  labelClassName,
  id,
  name,
  required = false,
  clearable = true,
  disabled = false,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search term
  const filteredOptions = options.filter(
    (option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (option.group &&
        option.group.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  // Group options by their group
  const groupedOptions: Record<string, SearchableSelectOption[]> = {};
  filteredOptions.forEach((option) => {
    const group = option.group || "Other";
    if (!groupedOptions[group]) {
      groupedOptions[group] = [];
    }
    groupedOptions[group].push(option);
  });

  // Handle outside clicks
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (disabled && (isOpen || searchTerm)) {
    setIsOpen(false);
    setSearchTerm("");
  }

  // Handle clear selection
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  // Find the selected option's label
  const selectedOption = options.find((option) => option.value === value);
  const displayValue = selectedOption ? selectedOption.label : "";

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label
          htmlFor={id}
          className={cn(
            "block text-sm font-medium text-foreground mb-1",
            labelClassName,
          )}
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <div
          className={cn(
            "px-4 py-2 bg-surface border border-border rounded-lg w-full transition-colors flex items-center justify-between cursor-pointer",
            error
              ? "border-red-500"
              : "focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent",
            disabled && "cursor-not-allowed opacity-60",
            className,
          )}
          id={id}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          onKeyDown={(e) => {
            if (disabled || e.target !== e.currentTarget) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsOpen((open) => !open);
            }
            if (e.key === "Escape") setIsOpen(false);
          }}
          onClick={() => {
            if (disabled) return;
            setIsOpen(!isOpen);
            if (!isOpen && inputRef.current) {
              setTimeout(() => inputRef.current?.focus(), 10);
            }
          }}
        >
          {!isOpen ? (
            <div className="flex-1 truncate flex items-center justify-between">
              <span>
                {displayValue || (
                  <span className="text-text-light">{placeholder}</span>
                )}
              </span>
              {clearable && displayValue && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="ml-2 text-text-light hover:text-text-light focus:outline-none"
                  aria-label="Clear selection"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          ) : (
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border-none p-0 focus:ring-0 focus:outline-none bg-transparent"
              placeholder="Search..."
              onClick={(e) => e.stopPropagation()}
              autoComplete="off"
              name={name}
              aria-label="Search"
            />
          )}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-5 w-5 text-text-light transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>

        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-surface border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
            {Object.keys(groupedOptions).length > 0 ? (
              Object.entries(groupedOptions).map(([group, options]) => (
                <div key={group}>
                  {Object.keys(groupedOptions).length > 1 && (
                    <div className="sticky top-0 px-4 py-2 text-xs font-semibold text-text-light bg-secondary">
                      {group}
                    </div>
                  )}
                  {options.map((option) => (
                    <div
                      key={option.value}
                      className="px-4 py-2 text-sm text-foreground hover:bg-tertiary cursor-pointer"
                      onClick={() => {
                        onChange(option.value);
                        setIsOpen(false);
                        setSearchTerm("");
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div className="px-4 py-2 text-sm text-text-light">
                No options found
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
